/**
 * Copyright © 2026 Delicious AI, LLC
 */

import {
  Close,
  SelectAll,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import type { AlertColor } from '@mui/material'
import type React from 'react'

import useT from '@/i18n/useT'
import CurrentImageCard from './CurrentImageCard'
import {
  DetectionClass,
  DetectionTagOption,
  DETECTION_CLASS_ORDER,
  getDetectionClassLabels,
  getDetectionClassColor,
} from './detectionUtils'

type DetectionSidebarProps = {
  imageId: string
  sidebarNotice?: React.ReactNode
  sidebarNoticeSeverity?: AlertColor
  sidebarFooter?: React.ReactNode
  activeClass: DetectionClass
  activeTag: DetectionTagOption
  filteredTags: DetectionTagOption[]
  hiddenClasses: Set<DetectionClass>
  counts: Record<DetectionClass, number>
  selectedCounts: Record<DetectionClass, number>
  showActiveTagSection: boolean
  canUseSearchAsTag: boolean
  tagSearch: string
  addingTag: boolean
  searchingTags: boolean
  loadingNext: boolean
  loadingImage: boolean
  showNextImageAction?: boolean
  onNextImage: () => void
  onActiveClassChange: (detectionClass: DetectionClass) => void
  onHoveredClassChange: (detectionClass: DetectionClass | null) => void
  onSelectBoxesForClass: (
    detectionClass: DetectionClass,
    mode?: 'select' | 'deselect',
  ) => void
  onToggleClassVisibility: (detectionClass: DetectionClass) => void
  onTagSearchChange: (value: string) => void
  onCreateTag: () => void
  onActiveTagChange: (tag: DetectionTagOption) => void
}

function DetectionSidebar({
  imageId,
  sidebarNotice = null,
  sidebarNoticeSeverity = 'warning',
  sidebarFooter = null,
  activeClass,
  activeTag,
  filteredTags,
  hiddenClasses,
  counts,
  selectedCounts,
  showActiveTagSection,
  canUseSearchAsTag,
  tagSearch,
  addingTag,
  searchingTags,
  loadingNext,
  loadingImage,
  showNextImageAction = true,
  onNextImage,
  onActiveClassChange,
  onHoveredClassChange,
  onSelectBoxesForClass,
  onToggleClassVisibility,
  onTagSearchChange,
  onCreateTag,
  onActiveTagChange,
}: DetectionSidebarProps) {
  const { t } = useT()
  const detectionClassLabels = getDetectionClassLabels(t)

  return (
    <Stack
      spacing={2}
      sx={{
        minHeight: 0,
        maxHeight: { lg: '100%' },
        overflowY: { lg: 'auto' },
        pr: { lg: 0.5 },
      }}
    >
      {sidebarNotice ? (
        <Alert severity={sidebarNoticeSeverity} sx={{ py: 0.5 }}>
          {sidebarNotice}
        </Alert>
      ) : null}
      <CurrentImageCard
        imageId={imageId}
        loadingImage={loadingImage}
        loadingNext={loadingNext}
        showNextImageAction={showNextImageAction}
        onNextImage={onNextImage}
      />

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">
              {t('detectionSidebar.labels.title', 'Labels')}
            </Typography>
            {DETECTION_CLASS_ORDER.map(detectionClass => {
              const classColor = getDetectionClassColor(
                detectionClass,
                activeTag.name,
              )
              const hidden = hiddenClasses.has(detectionClass)
              const count = counts[detectionClass]
              const selectedCount = selectedCounts[detectionClass]
              const classLabel = detectionClassLabels[detectionClass]
              const tooltipLabel = classLabel.toLowerCase()
              const allSelected = count > 0 && selectedCount === count
              return (
                <Box
                  key={detectionClass}
                  onMouseEnter={() => onHoveredClassChange(detectionClass)}
                  onMouseLeave={() => onHoveredClassChange(null)}
                  sx={theme => ({
                    p: 1.25,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor:
                      activeClass === detectionClass
                        ? classColor
                        : theme.palette.divider,
                    backgroundColor:
                      activeClass === detectionClass
                        ? `${classColor}16`
                        : theme.palette.background.paper,
                  })}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box
                      onClick={() => onActiveClassChange(detectionClass)}
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 99,
                        bgcolor: classColor,
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    />
                    <Box
                      onClick={() => onActiveClassChange(detectionClass)}
                      sx={{ minWidth: 0, cursor: 'pointer', flex: 1 }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {detectionClassLabels[detectionClass]}
                        </Box>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0 }}
                        >
                          {count}
                        </Typography>
                      </Typography>
                    </Box>
                    <Tooltip
                      title={
                        allSelected
                          ? t('detectionSidebar.labels.deselectAll.tooltip', {
                              label: tooltipLabel,
                              defaultValue: `Deselect all ${tooltipLabel} boxes`,
                            })
                          : t('detectionSidebar.labels.selectAll.tooltip', {
                              label: tooltipLabel,
                              defaultValue: `Select all ${tooltipLabel} boxes`,
                            })
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            allSelected
                              ? onSelectBoxesForClass(
                                  detectionClass,
                                  'deselect',
                                )
                              : onSelectBoxesForClass(detectionClass, 'select')
                          }
                          disabled={count === 0}
                          aria-label={
                            allSelected
                              ? t(
                                  'detectionSidebar.labels.deselectAll.ariaLabel',
                                  {
                                    label: tooltipLabel,
                                    defaultValue: `Deselect all ${tooltipLabel} boxes`,
                                  },
                                )
                              : t(
                                  'detectionSidebar.labels.selectAll.ariaLabel',
                                  {
                                    label: tooltipLabel,
                                    defaultValue: `Select all ${tooltipLabel} boxes`,
                                  },
                                )
                          }
                        >
                          {allSelected ? (
                            <Close fontSize="small" />
                          ) : (
                            <SelectAll fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={t('detectionSidebar.labels.visibility.tooltip', {
                        label: tooltipLabel,
                        action: hidden ? 'show' : 'hide',
                        defaultValue: `${hidden ? 'Show' : 'Hide'} ${tooltipLabel} boxes`,
                      })}
                    >
                      <IconButton
                        size="small"
                        onClick={() => onToggleClassVisibility(detectionClass)}
                        disabled={count === 0}
                        aria-label={t(
                          'detectionSidebar.labels.visibility.ariaLabel',
                          {
                            label: tooltipLabel,
                            action: hidden ? 'show' : 'hide',
                            defaultValue: `${hidden ? 'Show' : 'Hide'} ${tooltipLabel} boxes`,
                          },
                        )}
                      >
                        {hidden ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              )
            })}
          </Stack>
        </CardContent>
      </Card>

      {showActiveTagSection && (
        <Card>
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h6">
                {t('detectionSidebar.activeTag.title', 'Active Product Tag')}
              </Typography>
              <Chip
                size="small"
                label={t('detectionSidebar.activeTag.current', {
                  tag: activeTag.name,
                  defaultValue: `Active tag: ${activeTag.name}`,
                })}
                sx={{
                  alignSelf: 'flex-start',
                  borderColor: getDetectionClassColor(
                    'PRODUCT',
                    activeTag.name,
                  ),
                  color: getDetectionClassColor('PRODUCT', activeTag.name),
                }}
                variant="outlined"
              />
              <TextField
                size="small"
                value={tagSearch}
                onChange={event => onTagSearchChange(event.target.value)}
                placeholder={t(
                  'detectionSidebar.activeTag.searchPlaceholder',
                  'Search or add a tag',
                )}
              />
              <Stack spacing={1} sx={{ maxHeight: 168, overflowY: 'auto' }}>
                {canUseSearchAsTag && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onCreateTag}
                    sx={{ justifyContent: 'flex-start' }}
                    disabled={addingTag}
                  >
                    {t('detectionSidebar.activeTag.useTag', {
                      tag: tagSearch.trim(),
                      defaultValue: `Use "${tagSearch.trim()}"`,
                    })}
                  </Button>
                )}
                {filteredTags.map(tag => {
                  const active = tag.id === activeTag.id
                  const tagColor = getDetectionClassColor('PRODUCT', tag.name)
                  return (
                    <Box
                      key={tag.id}
                      role="button"
                      onClick={() => onActiveTagChange(tag)}
                      sx={theme => ({
                        p: 1.25,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: active ? tagColor : theme.palette.divider,
                        backgroundColor: active
                          ? `${tagColor}16`
                          : theme.palette.background.paper,
                        cursor: 'pointer',
                      })}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: 99,
                            bgcolor: tagColor,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2">{tag.name}</Typography>
                      </Stack>
                    </Box>
                  )
                })}
                {!filteredTags.length && !searchingTags && (
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'detectionSidebar.activeTag.noTagsFound',
                      'No tags found.',
                    )}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {sidebarFooter}
    </Stack>
  )
}

export default DetectionSidebar
